<template>
 <div class="md:hidden ">
  <MobileSearchBar />
</div>
  <!-- ✅ Fixed banner height for better visibility -->
  <div
    class="relative overflow-hidden bg-white"
    style="width: 100vw; margin-left: calc(50% - 50vw)"
    @mousedown="startDrag"
    @mousemove="handleDrag"
    @mouseup="endDrag"
    @mouseleave="endDrag"
    @touchstart="startDrag"
    @touchmove="handleDrag"
    @touchend="endDrag"
  >
    <div
      ref="sliderTrack"
      class="flex transition-transform duration-500 ease-in-out"
      :class="{ 'flex-row-reverse': isRTL }"
      :style="{
        transform: `translateX(calc(-${activeSlide * 100}% + ${
          isDragging ? dragOffset : 0
        }%))`,
        transition: isDragging ? 'none' : 'transform 500ms ease-in-out',
      }"
    >
      <div
        v-for="(slide, index) in slides"
        :key="index"
        class="w-screen flex-shrink-0 overflow-hidden"
        :class="{
          'h-[20vh] sm:h-[30vh] md:h-[50vh] lg:h-[55vh] xl:h-[55vh] xl:mt-10': true,
        }"
        :style="{
          backgroundImage: `url(${slide.image})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }"
      >
        <!-- You can optionally place content inside here -->
      </div>
    </div>
  </div>

  <div class="min-h-screen bg-gray-50">
    <!-- ✅ Redesigned Hero Banner Section -->
    <div class="relative"></div>

    <!-- ✅ Enhanced Categories Section - Reduced top padding -->
<section class="py-0 sm:py-6 bg-white pt-2">
  <div class="max-w-7xl mx-auto px-4 sm:px-6">
    <h2 class="text-2xl font-bold text-start mb-2">
      {{ $t("home.shopByCategories") }}
    </h2>

    <div class="categories-container">
      <!-- Row 1: mainCategory = 1 or 2 -->

<div class="flex justify-center mb-4">
  <div class="grid grid-cols-2 gap-6" style="max-width: 900px; width: 100%;">
    <router-link
      v-for="cat in topRowCategories"
      :key="cat.id"
      :to="{ name: 'Products', query: { category_id: cat.id } }"
      class="block w-full rounded-md overflow-hidden shadow-sm border hover:shadow-md transition bg-white"
    >
      <!-- More rectangular shape -->
      <div class="flex items-center justify-center bg-gray-50" style="height: 140px;">
        <img
          :src="getOptimizedImage(cat.image)"
          :alt="cat.name"
          class="max-h-[70%] max-w-[70%] object-contain"
          loading="lazy"
        />
      </div>

      <!-- Text -->
      <div class="p-3 text-center">
        <p class="text-sm sm:text-base font-medium truncate">{{ cat.name }}</p>
      </div>
    </router-link>
  </div>
</div>






      <!-- Row 2: mainCategory = 0 (CAROUSEL) -->
<div class="relative">
  <button
    type="button"
    class="hidden sm:flex absolute -left-2 top-1/2 -translate-y-1/2 z-10 rounded-full border bg-white/90 px-2 py-2 shadow hover:bg-white"
    @click="scrollCarousel('left')"
  >
    ‹
  </button>
  <button
    type="button"
    class="hidden sm:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10 rounded-full border bg-white/90 px-2 py-2 shadow hover:bg-white"
    @click="scrollCarousel('right')"
  >
    ›
  </button>

  <div
    ref="catCarousel"
    class="flex gap-2 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 hide-scrollbar"
  >
    <router-link
      v-for="cat in bottomCategories"
      :key="cat.id"
      :to="{ name: 'Products', query: { category_id: cat.id } }"
      class="snap-start shrink-0 w-24 sm:w-28 rounded-md overflow-hidden shadow-sm border hover:shadow-md transition bg-white"
    >
      <!-- Smaller icon area -->
      <div class="flex items-center justify-center bg-gray-50" style="height: 80px;">
        <img
          :src="getOptimizedImage(cat.image)"
          :alt="cat.name"
          class="max-h-[60%] max-w-[60%] object-contain"
          loading="lazy"
        />
      </div>
      <div class="p-1 text-center">
        <p class="text-xs font-medium truncate">{{ cat.name }}</p>
      </div>
    </router-link>
  </div>
</div>

    </div>
  </div>
</section>





    
    <!-- ✅ Featured Products Slider Section -->
    <section class="py-0 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6">
        <div class="flex justify-start items-center mb-0">
          <h2 class="text-2xl font-bold">{{ $t("home.featuredProducts") }}</h2>
        </div>

        <!-- ✅ Scrollable area -->
        <!-- ✅ Featured Products Scrollable Slider (with manual drag support) -->
        <div
          ref="featuredSlider"
          class="overflow-hidden cursor-grab active:cursor-grabbing select-none"
          @mousedown="startFeaturedDrag"
          @mousemove="handleFeaturedDrag"
          @mouseup="endFeaturedDrag"
          @mouseleave="endFeaturedDrag"
          @touchstart="startFeaturedDrag"
          @touchmove="handleFeaturedDrag"
          @touchend="endFeaturedDrag"
        >
          <div
            ref="featuredTrack"
            class="flex gap-4 px-4 transition-all duration-300 ease-in-out p-5"
            style="scroll-snap-type: x mandatory"
          >
            <ProductCard
              v-for="product in featuredProducts"
              :key="product.id"
              :product="product"
              class="min-w-[280px] sm:min-w-[300px] md:min-w-[320px] snap-start"
              :is-in-cart="isInCart(product.id)"
              :is-in-wishlist="isInWishlist(product.id)"
              @add-to-cart="addToCart"
              @toggle-wishlist="toggleWishlist"
              @open-chat="handleChatOpen"
            />
          </div>
        </div>
      </div>
    </section>


    <!-- ✅ Recently Added Products Section -->
    <section class="py-0 mb-5 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 pb-5">
        <div class="flex justify-between items-center mb-2 py-4">
          <h2 class="text-2xl font-bold">{{ $t("home.recentlyAddedProducts") }}</h2>
          <router-link
            to="/products?sort=recent"
            class="text-teal-600 hover:text-teal-800 font-medium flex items-center"
          >
            {{ $t("home.viewAll") }}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 ml-1 transform"
              :class="{ 'rotate-180': $i18n.locale === 'ar' }"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </router-link>
        </div>

        <div
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
        >
          <ProductCard
            v-for="product in recentlyAddedProducts"
            :key="product.id"
            :product="product"
            :is-in-cart="isInCart(product.id)"
            :is-in-wishlist="isInWishlist(product.id)"
            @add-to-cart="addToCart"
            @toggle-wishlist="toggleWishlist"
            @open-chat="handleChatOpen"
          />
        </div>
      </div>
    </section>

    <!-- ✅ Trending Products Section -->
    <section class="py-0 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 pb-5">
        <div class="flex justify-between items-center mb-2 py-4">
          <h2 class="text-2xl font-bold">{{ $t("home.trendingProducts") }}</h2>
          <router-link
            to="/products"
            class="text-teal-600 hover:text-teal-800 font-medium flex items-center"
          >
            {{ $t("home.viewAll") }}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 ml-1 transform"
              :class="{ 'rotate-180': $i18n.locale === 'ar' }"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </router-link>
        </div>

        <div
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
        >
          <ProductCard
            v-for="product in trendingProducts"
            :key="product.id"
            :product="product"
            :is-in-cart="isInCart(product.id)"
            :is-in-wishlist="isInWishlist(product.id)"
            @add-to-cart="addToCart"
            @toggle-wishlist="toggleWishlist"
            @open-chat="handleChatOpen"
          />
        </div>
      </div>
    </section>

    <!-- ✅ Features Section -->
    <section class="py-0 bg-gray-50 pb-5">
      <div class="max-w-7xl mx-auto px-4 sm:px-6">
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 pt-10">
          <div
            v-for="(feature, index) in features"
            :key="index"
            class="flex items-start p-4 rounded-lg hover:bg-gray-50 transition-all"
          >
            <div
              class="bg-teal-100 p-3 rounded-full ltr:mr-4 rtl:ml-4 text-teal-600"
            >
              <component :is="feature.icon" class="h-6 w-6" />
            </div>
            <div>
              <h3 class="font-bold text-gray-800">{{ feature.title }}</h3>
              <p class="text-gray-500 text-sm mt-1">
                {{ feature.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import api from "@/api";

import {
  TruckIcon,
  ArrowPathIcon,
  GiftIcon,
  PhoneIcon,
} from "@heroicons/vue/24/outline";
import ProductCard from "@/components/ProductCard.vue";
import { inject, ref, computed } from "vue";
import eventBus from "@/eventBus";
import MobileSearchBar from "@/components/MobileSearchBar.vue";
export default {
  setup() {
    const cartStore = inject("cartStore");
    const selectedLang = ref(localStorage.getItem("lang_code") || "ar");
    const isRTL = computed(() => selectedLang.value === "ar");
    const sliderTrack = ref(null);
    return { cartStore, selectedLang, isRTL, sliderTrack };
  },
  name: "HomePage",
  components: { TruckIcon, ArrowPathIcon, GiftIcon, PhoneIcon, ProductCard ,MobileSearchBar },
  data() {
    return {
      activeSlide: 0,
      autoSlideInterval: null,
      slides: [],
      categories: [],
      trendingProducts: [],
      featuredProducts: [],
      recentlyAddedProducts: [],
      isDragging: false,
      startPosX: 0,
      currentPosX: 0,
      dragOffset: 0,
      dragThreshold: 50,
      pauseTimeout: null,
      isFeaturedDragging: false,
      featuredStartX: 0,
      featuredScrollLeft: 0,
    };
  },
  computed: {
    features() {
      return [
        {
          icon: TruckIcon,
          title: this.$t("home.features.freeDelivery.title"),
          description: this.$t("home.features.freeDelivery.desc"),
        },
        {
          icon: ArrowPathIcon,
          title: this.$t("home.features.returnRefund.title"),
          description: this.$t("home.features.returnRefund.desc"),
        },
        {
          icon: GiftIcon,
          title: this.$t("home.features.memberDiscount.title"),
          description: this.$t("home.features.memberDiscount.desc"),
        },
        {
          icon: PhoneIcon,
          title: this.$t("home.features.support.title"),
          description: this.$t("home.features.support.desc"),
        },
      ];
    },

    // Row 1: show 1 and 2 together (sorted so 1 comes before 2)
      topRowCategories() {
        return this.categories
          .filter(c => [1, 2].includes(Number(c.mainCategory)))
          .sort((a, b) => Number(a.mainCategory) - Number(b.mainCategory));
      },
      // Row 2: show only 0
      bottomCategories() {
        return this.categories.filter(c => Number(c.mainCategory) === 0);
      },
  },
  
  mounted() {
   this.loadInitialData();
     this.fetchCategories();

  },
  beforeUnmount() {
    clearInterval(this.autoSlideInterval);
    clearTimeout(this.pauseTimeout);
  },
  methods: {
    async loadInitialData() {
    try {
      await Promise.all([
        this.fetchCategories(),
        this.fetchTrendingProducts(),
        this.fetchRecentlyAddedProducts(),
        this.fetchFeaturedProducts(),
        this.fetchBanners()
      ]);
      this.startAutoSlide(); // ✅ شغل السلايدر بعد اكتمال التحميل فعليًا
    } catch (error) {
      console.error("Error loading homepage data:", error);
    }
  },
    startFeaturedDrag(e) {
      this.isFeaturedDragging = true;
      const slider = this.$refs.featuredSlider;
      this.featuredStartX = e.type.includes("touch")
        ? e.touches[0].pageX - slider.offsetLeft
        : e.pageX - slider.offsetLeft;

      this.featuredScrollLeft = slider.scrollLeft;
    },
    handleFeaturedDrag(e) {
      if (!this.isFeaturedDragging) return;

      e.preventDefault();
      const slider = this.$refs.featuredSlider;
      const x = e.type.includes("touch")
        ? e.touches[0].pageX - slider.offsetLeft
        : e.pageX - slider.offsetLeft;
      const walk = (x - this.featuredStartX) * 1.5; // multiplier = scroll speed
      slider.scrollLeft = this.featuredScrollLeft - walk;
    },
    endFeaturedDrag() {
      this.isFeaturedDragging = false;
    },
  async fetchFeaturedProducts() {
  try {
    const response = await api.get("/v1/featuredItems");

    const rawData = Object.values(response.data);

    const products = rawData.map((item) => ({
      id: item.id,
      name: item.title,
      description: item.description,
      price: parseFloat(item.price),
      images: Array.isArray(item.image)
        ? item.image
        : JSON.parse(item.image),
      discount: null,
      rating: 4,
      reviews: item.fields?.length || 0,
      user_id: item.user?.id ?? null,
    }));

    this.featuredProducts = products;
  } catch (error) {
    console.error("Failed to fetch featured products:", error);
  }
},
    // Swipe/drag methods
    startDrag(e) {
      this.isDragging = true;
      this.startPosX = this.getClientX(e);
      this.currentPosX = this.startPosX;

      // Fully pause auto-slide and any pending resume
      clearInterval(this.autoSlideInterval);
      clearTimeout(this.pauseTimeout);
    },
    handleDrag(e) {
      if (!this.isDragging) return;

      e.preventDefault();
      this.currentPosX = this.getClientX(e);
      this.dragOffset =
        ((this.currentPosX - this.startPosX) / window.innerWidth) * 100;

      // Limit dragging to prevent excessive movement
      const maxOffset = 30; // Max 30% drag offset
      if (Math.abs(this.dragOffset) > maxOffset) {
        this.dragOffset = this.dragOffset > 0 ? maxOffset : -maxOffset;
      }
    },

    endDrag() {
      if (!this.isDragging) return;

      this.isDragging = false;
      const distance = this.currentPosX - this.startPosX;

      if (Math.abs(distance) > this.dragThreshold) {
        if (distance > 0) {
          this.prevSlide(); // calls resetAutoSlide()
        } else {
          this.nextSlide(); // calls resetAutoSlide()
        }
      }

      this.dragOffset = 0;

      // Delay resuming auto-slide to avoid conflict
      this.pauseTimeout = setTimeout(() => {
        this.startAutoSlide();
      }, 2000); // delay to let transition finish
    },
    getClientX(e) {
      return e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    },
    getOptimizedImage(imageUrl) {
      if (!imageUrl) {
        return "https://admin.from-malk.com/uploads/default.jpg";
      }

      if (imageUrl.startsWith("http")) {
        return imageUrl;
      }

      return `https://admin.from-malk.com/uploads/${imageUrl}`;
    },
    handleChatOpen(payload) {
      const { userId, product } = payload;
      eventBus.emit("open-chat", { userId, product });
    },
    async fetchBanners() {
      try {
        const response = await api.get("/v1/banners");
        this.slides = response.data.map((banner) => ({
          title: banner.title,
          price: banner.price,
          discount: banner.discount,
          image: `https://admin.from-malk.com/storage/${banner.image}`,
          link: banner.link,
        }));
      } catch (error) {
        console.error("Failed to fetch banners:", error);
      }
    },
    async fetchCategories() {
      try {
        const response = await api.get("/v1/categories");
        this.categories = response.data.map((cat) => ({
          id: cat.id,
          name: cat.name,
          image: cat.image,
          mainCategory: Number(cat.step),
        }));
        console.log(this.categories);
      } catch (error) {
        console.error("Failed to load categories:", error);
      }
    },
     scrollCarousel(direction) {
        const el = this.$refs.catCarousel;
        if (!el) return;
        const amount = el.clientWidth * 0.9;
        el.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
      },

    async fetchTrendingProducts() {
      try {
        const response = await api.get("/v1/random-limited");
        const allProducts = response.data.map((item) => ({
          id: item.id,
          name: item.title,
          description: item.description,
          price: parseFloat(item.price),
          images: Array.isArray(item.image)
            ? item.image
            : JSON.parse(item.image),
          discount: null,
          rating: 4,
          reviews: item.fields?.length || 0,
          user_id: item.user?.id ?? null,
        }));

        const shuffled = allProducts.sort(() => 0.5 - Math.random());
        this.trendingProducts = shuffled.slice(0, 8);
      } catch (error) {
        console.error("Failed to load products:", error);
      }
    },
    async fetchRecentlyAddedProducts() {
      try {
        const response = await api.get("/v1/recentlyItems");
        const products = response.data.map((item) => ({
          id: item.id,
          name: item.title,
          description: item.description,
          price: parseFloat(item.price),
          images: Array.isArray(item.image)
            ? item.image
            : JSON.parse(item.image),
          discount: null,
          rating: 4,
          reviews: item.fields?.length || 0,
          user_id: item.user?.id ?? null,
        }));
        this.recentlyAddedProducts = products;
      } catch (error) {
        console.error("Failed to fetch recently added products:", error);
      }
    },
    nextSlide() {
      if (this.activeSlide < this.slides.length - 1) {
        this.activeSlide++;
      } else {
        this.activeSlide = 0;
      }
    },

    prevSlide() {
      if (this.activeSlide > 0) {
        this.activeSlide--;
      } else {
        this.activeSlide = this.slides.length - 1;
      }
    },
    disableTransition() {
      if (this.sliderTrack && this.sliderTrack.$el) {
        this.sliderTrack.$el.style.transition = "none";
      } else if (this.sliderTrack && this.sliderTrack instanceof HTMLElement) {
        this.sliderTrack.style.transition = "none";
      }
    },
    enableTransition() {
      if (this.sliderTrack && this.sliderTrack.$el) {
        this.sliderTrack.$el.style.transition = "";
      } else if (this.sliderTrack && this.sliderTrack instanceof HTMLElement) {
        this.sliderTrack.style.transition = "";
      }
    },
    startAutoSlide() {
      clearInterval(this.autoSlideInterval);
      this.autoSlideInterval = setInterval(() => {
        this.nextSlide();
      }, 4000);
    },
    resetAutoSlide() {
      clearInterval(this.autoSlideInterval);
      this.startAutoSlide();
    },
    addToCart(product) {
      this.cartStore.addToCart(product);
    },
    toggleWishlist(productId) {
      this.cartStore.toggleWishlist(productId);
    },
    isInCart(productId) {
      return this.cartStore.isInCart(productId);
    },
    isInWishlist(productId) {
      return this.cartStore.isInWishlist(productId);
    },
  },
};
</script>

<style scoped>
img {
  max-height: 127px;
  user-select: none;
  -webkit-user-drag: none;
}

/* Make banner container non-selectable */
.relative.w-full.overflow-hidden.bg-white {
  user-select: none;
  cursor: grab;
}

.relative.w-full.overflow-hidden.bg-white:active {
  cursor: grabbing;
}

/* Smooth transition when dragging ends */
.transition-transform {
  will-change: transform;
}
/* Hero Banner Styles */
.relative {
  position: relative;
}
.relative.h-\[500px\] {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 500px;
}
.h-\[500px\] {
  height: 600px;
}

@media (min-width: 1024px) {
  .h-\[500px\] {
    height: 600px;
  }
}

/* Slide transition */
.transition-transform {
  transition-property: transform;
}

.duration-500 {
  transition-duration: 500ms;
}

.ease-in-out {
  transition-timing-function: ease-in-out;
}

/* Overlay for better text readability */
.bg-black\/30 {
  background-color: rgba(0, 0, 0, 0.3);
}

/* Navigation arrows */
.absolute {
  position: absolute;
}

.z-20 {
  z-index: 20;
}

/* Categories Section - Enhanced Responsive Design */
.categories-container {
  width: 100%;
  overflow-x: auto;
  padding-bottom: 1rem;
  scroll-snap-type: x mandatory;
}

.categories-grid {
  display: flex;
  gap: 1rem;
  padding: 0.5rem 1rem;
  -webkit-overflow-scrolling: touch;
  scroll-padding: 1rem;
}

.category-card {
  flex: 0 0 calc(45% - 0.5rem);
  min-width: 0;
  height: 200px;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  overflow: hidden;
  background-color: white;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #eeeeee;
}

.category-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

/* Tablet and larger screens */
@media (min-width: 640px) {
  .categories-container {
    overflow-x: visible;
    padding-bottom: 0;
  }

  .categories-grid {
    flex-wrap: wrap;
    justify-content: center;
    gap: 1.25rem;
    padding: 0;
    padding-bottom: 2em;
  }

  .category-card {
    flex: 0 0 calc(50% - 1.25rem);
    max-width: calc(50% - 1.25rem);
  }
}

@media (min-width: 768px) {
  .category-card {
    flex: 0 0 calc(33.333% - 1.25rem);
    max-width: calc(33.333% - 1.25rem);
  }
}

@media (min-width: 1024px) {
  .category-card {
    flex: 0 0 calc((100% - 3rem) / 5);
    max-width: calc((100% - 3rem) / 5);
  }
}

@media (min-width: 1280px) {
  .category-card {
    flex: 0 0 calc((100% - 3rem) / 5);
    max-width: calc((80% - 3rem) / 5);
  }
}

/* Image and content styling */
.image-container {
  width: 100%;
  aspect-ratio: 1/1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: transparent;
  position: relative;
}

.category-image {
  width: 90%;
  height: 88%;
  transition: transform 0.3s ease;
  -o-object-fit: contain;
  object-fit: contain;
  padding: 0;
  border-radius: 15px;
  border: 1px solid #f3f4f6;
}

.object-cover {
  object-fit: cover;
}

.category-content {
  padding: 0.75rem;
  /* background-color: #f9fafb; */
  text-align: center;
  /* border-top: 1px solid #f3f4f6; */
}

.category-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #1f2937;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.shop-text {
  font-size: 0.75rem;
  color: #6b7280;
  transition: color 0.2s ease;
  font-weight: 500;
}

.category-card:hover .shop-text {
  color: #0d9488;
}

/* Hide scrollbar but keep functionality */
.categories-container::-webkit-scrollbar {
  display: none;
}

.categories-container {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Product card hover animation */
.category-card:hover .category-image {
  transform: scale(1.05);
}

/* Loading skeleton for images */
.image-container:empty::before {
  content: "";
  display: block;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Responsive text sizes */
@media (min-width: 640px) {
  .category-name {
    font-size: 1rem;
  }

  .shop-text {
    font-size: 0.875rem;
  }
}

/* Special case for single category */
.categories-grid:has(.category-card:only-child) .category-card {
  flex: 0 0 280px;
  max-width: 280px;
}

/* Features section hover effect */
.hover\:bg-gray-50:hover {
  background-color: #f9fafb;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

/* Smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Button hover effects */
.hover\:bg-gray-100:hover {
  background-color: #f3f4f6;
  transform: translateY(-1px);
}

/* Shadow effects */
.shadow-lg {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.hover\:shadow-xl:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
}
/* Hide scrollbar for all browsers */
.scrollbar-hide {
  -ms-overflow-style: none; /* IE & Edge */
  scrollbar-width: none; /* Firefox */
}
.scrollbar-hide::-webkit-scrollbar {
  display: none; /* Chrome, Safari */
}

.snap-start {
  scroll-snap-align: start;
}

.hide-scrollbar {
  -ms-overflow-style: none; /* IE/Edge */
  scrollbar-width: none;    /* Firefox */
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;            /* Chrome/Safari */
}

</style>
